import React from "react";
import { Link } from "react-router-dom";
import { validateGender } from "../../utils/results";
import BetaHistogram from "../../components/beta-histogram";
import TagPercentile from "../../components/tag-percentile";

const TestosteroneResults = ({
  result,
  userName,
  nameEndsWithS,
  demographics,
  userIsMe,
  crm,
}) => {
  const hasDemographics = validateGender(demographics);
  let testComponent;
  if (hasDemographics && result["value"] != null) {
    let gender = demographics.gender;
    let configKey = `${demographics.gender}-testosterone`;
    let crmKey = `results-${configKey}-histogram`;
    let prefixedGeneticScore =
      result["value"] > 0
        ? `+${result["value"].toFixed(2)}`
        : `${result["value"].toFixed(2)}`;
    // let polyScore = result['estScore'];
    let polyScore = result["value"];
    let prefixedPolyScore =
      polyScore > 0 ? `+${polyScore.toFixed(0)}` : `${polyScore.toFixed(0)}`;

    let graphComment, snpAmt, sd, corr;
    if (configKey === "male-testosterone") {
      // normalComment = "The normal range for testosterone is between 8.7-33 ng/dL for men.";
      snpAmt = "in males using 122 genetic markers";
      graphComment = "The graph shows a histogram of scores from 194,453 men. ";
      sd = "0.70";
      corr = "34%";
    } else if (configKey === "female-testosterone") {
      // normalComment = "The normal range for testosterone is between 10-60 ng/dL for women.";
      snpAmt = "in females using 240 genetic markers";
      graphComment =
        "The graph shows a histogram of scores from 230,454 women.";
      sd = "0.11";
      corr = "21%";
    }

    testComponent = (
      <div>
        <div className="card">
          <p>
            AxGen has developed an algorithm to predict testosterone levels{" "}
            {snpAmt} from{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://pubmed.ncbi.nlm.nih.gov/32042192/"
            >
              Ruth et al., 2020
            </a>
            .
          </p>

          <BetaHistogram result={result} crmKey={crmKey} crm={crm} />
          <p>
            {graphComment} Your score for testosterone levels is{" "}
            <strong>{prefixedGeneticScore}</strong> nmol/L.
            {configKey === "male-testosterone"
              ? " The median PRS score for men (predicted testosterone from genetics) is 13.02 nmol/L. The normal range for testosterone measured in a lab is 8 to 30 nmol/L for men."
              : configKey === "female-testosterone"
                ? " The median PRS score for women (predicted testosterone from genetics) is 1.11 nmol/L. The normal range for testosterone measured in a lab is 0.3 to 2.0 nmol/L for women."
                : ""}
          </p>
          <p className="mb-4">
            The genetic score will be within ~{sd} nmol/L of your testosterone
            levels 68% of the time. The correlation of the testosterone genetic
            score with observed levels of testosterone is {corr}. This means
            that genetics provides the baseline, but factors like age, diet and
            training account for most of the variation in testosterone
            levels.{" "}
          </p>
          <p>
            <TagPercentile
              result={result}
              tag={configKey}
              polygenic={true}
              usesUKData={false}
            />
          </p>

          <p className="mb-0"></p>
        </div>
        <div className="title-break">
          <span>Calibration of the testosterone PRS score</span>
        </div>
        <div className="card">
          <img
            alt=""
            src={
              configKey === "male-testosterone"
                ? "/assets/imgs/results/content/male_calPlot.png"
                : configKey === "female-testosterone"
                  ? "/assets/imgs/results/content/female_calPlot.png"
                  : ""
            }
            height={400}
            className="overview-img"
          />
          <p>
            To ensure our results are reliable, we compared our{" "}
            <strong>genetic predictions</strong> (x-axis) against{" "}
            <strong>actual lab-measured levels</strong> (y-axis).
          </p>
          <ul>
            <li>
              <strong>The Dots:</strong> Represent groups of {gender}s
              (deciles). Their close proximity to the line shows high predictive
              consistency.
            </li>
            <li>
              <strong>The Red Line:</strong> Represents "Perfect
              Calibration"—where genetics and lab results align 1:1.
            </li>
            <li>
              <strong>The Gray Bars:</strong> Indicate the 95% confidence
              interval for each group.
            </li>
          </ul>
        </div>
      </div>
    );
  } else {
    if (userIsMe) {
      testComponent = (
        <div className="card">
          <p className="mb-0">
            We invite you to input your sex (male or female) information{" "}
            <Link to="/settings">here</Link> for a comprehensive analysis of
            your testosterone levels.
          </p>
        </div>
      );
    } else {
      testComponent = (
        <div className="card">
          <p className="mb-0">
            The person whose profile you are viewing has not input their
            demographic information. Please ask them to do so in their Account
            Settings in order to view a comprehensive analysis of testosterone
            levels.
          </p>
        </div>
      );
    }
  }

  return (
    <div>
      <div className="title-margins">
        <h2>
          {userName ? `${userName}'${nameEndsWithS ? "" : "s"}` : ""} Results
          for Testosterone Levels
        </h2>
      </div>
      {testComponent}
    </div>
  );
};

export default TestosteroneResults;
